import React, { FC, useEffect, useState } from "react"
import { Script } from "gatsby"

import { generateConditionalClassName, isClientSide } from "@utils"

import useWistiaScript from "./useWistiaScript"

import "./Wistia.scss"

type WistiaProps = {
  aspectratio?: number
  autoPlay?: boolean
  className?: string
  id: string
  muted?: boolean
  onVideoEnd?: () => void
  onVideoPlay?: () => void
}

const Wistia: FC<WistiaProps> = ({
  autoPlay,
  className,
  id,
  muted,
  onVideoEnd,
  onVideoPlay,
}) => {
  const isWistiaReady = useWistiaScript(true)
  const [aspectRatio, setAspectRatio] = useState<number>(16 / 9)

  useEffect(() => {
    if (!isClientSide() || !isWistiaReady || !window.Wistia) return

    const timeout = Date.now() + 3000

    const waitForVideo = () => {
      const videoObj = window.Wistia.api(id)

      if (Date.now() >= timeout) return

      if (!videoObj) {
        setTimeout(waitForVideo, 100)
        return
      }

      const _aspect = videoObj.aspect()

      if (videoObj.data?.media?.mediaType === "Audio") {
        setAspectRatio(16 / 9)
        return
      }

      if (_aspect === 0) {
        setTimeout(waitForVideo, 100)
        return
      }

      if (onVideoEnd) videoObj.bind("end", onVideoEnd)
      if (onVideoPlay) videoObj.bind("play", onVideoPlay)

      setAspectRatio(_aspect)
    }

    waitForVideo()
  }, [isWistiaReady])

  return (
    <>
      {isWistiaReady && (
        <Script src={`https://fast.wistia.com/embed/medias/${id}.jsonp`} />
      )}
      <div
        className={generateConditionalClassName({
          Wistia: true,
          [`${className}`]: !!className,
          "is-wistia-ready": isWistiaReady,
        })}
        style={{
          "--Wistia--aspect-ratio": aspectRatio,
        }}
      >
        <div className="Wistia--Responsive">
          <div
            className={`Wistia--Embed wistia_embed wistia_async_${id} videoFoam=false${autoPlay ? " autoPlay=true" : "autoPlay=false"
              }${muted ? " muted=true" : ""}`}
          />
        </div>
      </div>
    </>
  )
}

export default Wistia
